import type { College, FilterRecord, RGBColor } from "../types";
import type { BrandStandards } from "../types/colleges";
import type {
  ComplianceMerchItem,
  MockFile,
  ProductVersion,
} from "../types/merchandise";

/** Data Parsing Helper functions */

export const stateMap: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

type StateAbbreviation = keyof typeof stateMap;

export function getStateName(abbr: StateAbbreviation): string {
  return stateMap[abbr];
}

export const isActionRequired = (collegeData?: College): boolean => {
  if (!isInternalLicense(collegeData?.licensing_company)) {
    return false;
  }

  if (!collegeData?.licenseeInfo) {
    return true;
  }

  if (!collegeData?.licenseeInfo?.termsAgreed) {
    return true;
  }

  if (!collegeData.brandStandards) {
    return true;
  }

  return !isBrandStandardsComplete(collegeData.brandStandards);
};

export function createMockImageFile(name: string, url: string): MockFile {
  return {
    name,
    url,
    type: "image/png",
  };
}

function createMockSvgFile(fileName: string, url: string) {
  return {
    name: fileName,
    url: url,
    type: "image/svg+xml",
    renderable: true,
  };
}

function createMockNonRenderableFile(
  fileName: string,
  url: string,
  extension: string
) {
  return {
    name: fileName,
    url: url,
    type: extension,
    renderable: false,
  };
}

export const isInternalLicense = (licensingCompany: string) => {
  if (["NONE", "Internal", "LegacyInternal","ExternalWithException"].includes(licensingCompany))
    return true;
  return false;
};

const getMostRecentCommentTime = (item) => {
  const allComments = item.versions.reduce((acc, version) => {
    return acc.concat(version.comments || []);
  }, []);

  if (allComments.length > 0) {
    allComments.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    return new Date(allComments[0].timestamp).getTime();
  }

  return new Date(item.versions[0]?.created_at || 0).getTime();
};

export function aggregateMerchItems(data): ComplianceMerchItem[] {
  if (!data || !Array.isArray(data)) return [];

  const skuGroups = {};

  // Group mockups by SKU
  data.forEach((item) => {
    const mockup = item.license_mockup;
    const groupKey = `${mockup.sku_name}|${mockup.school_name}`;

    if (!skuGroups[groupKey]) {
      skuGroups[groupKey] = [];
    }

    // Add the entire item (mockup, media, comments) to the group
    skuGroups[groupKey].push(item);
  });

  const merchItems = [];

  Object.keys(skuGroups).forEach((groupKey) => {
    const items = skuGroups[groupKey];
    const [sku, schoolName] = groupKey.split("|");

    // Sort items by mockup creation date (newest first)
    items.sort(
      (a, b) =>
        new Date(b.license_mockup.created_at).getTime() -
        new Date(a.license_mockup.created_at).getTime()
    );

    const mostRecentItem = items[0];
    const mostRecentMockup = mostRecentItem.license_mockup;

    const versions = items.map((item) => {
      const mockup = item.license_mockup;
      const media = item.license_mockup_media || [];
      const comments = item.mockup_comments || [];

      comments.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      const formattedComments = comments.map((comment) => {
        return {
          user: comment.commenter_name,
          comment: comment.comment,
          timestamp: convertToLocalTime(comment.created_at),
        };
      });

      return {
        files: media.map((m) => {
          const urlParts = m?.media_url?.split(".");
          const extension =
            urlParts?.length > 1
              ? urlParts[urlParts.length - 1].toLowerCase().split("/")[0]
              : "";

          const fileName = `file_${m.idLicenseMockupMedia}.${extension}`;

          if (
            ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(extension)
          ) {
            return createMockImageFile(fileName, m.media_url);
          } else if (extension === "svg") {
            return createMockSvgFile(fileName, m.media_url);
          } else {
            return createMockNonRenderableFile(
              fileName,
              m.media_url,
              extension
            );
          }
        }),
        comments: formattedComments,
        createdAt: convertToLocalTime(mockup.created_at),
        modifiedAt: convertToLocalTime(mockup.modified_at),
        mockupID: mockup.idLicenseMockup,
        status: mockup.state,
      };
    });

    const revisions = items.length - 1;

    const merchItem = {
      sku: sku,
      revisions: revisions,
      status: mostRecentMockup.state,
      productType: mostRecentMockup.product_type,
      color: mostRecentMockup.color,
      logoApplication: mostRecentMockup.logo_application,
      image: mostRecentMockup.mockup_url,
      versions: versions,
      college: schoolName,
      lastUpdated: new Date(mostRecentMockup.created_at),
    };

    const lastUpdatedTimestamp = getLastUpdatedTimestamp(merchItem);

    merchItem.lastUpdated = lastUpdatedTimestamp;

    merchItems.push(merchItem);
  });

  merchItems.sort((a, b) => {
    const aRecentTime = getMostRecentCommentTime(a);
    const bRecentTime = getMostRecentCommentTime(b);

    return bRecentTime - aRecentTime;
  });

  return merchItems;
}

export function mapBrandStandards(data): BrandStandards | null {
  if (!data?.brand_standards || !data?.brand_standards?.idBrandStandards) {
    console.log(`returning`);
    return null;
  }

  const newBrandStandards: BrandStandards = {
    BrandColors: [],
    BrandExpressions: [],
    BrandFonts: [],
    BrandLogos: [],
    BrandStandards: {
      brandStandardsFileUrl:
        data.brand_standards?.brand_standards_file_url || "",
      brandUsage: data.brand_standards?.brand_usage || "",
      idBrandStandards: data.brand_standards.idBrandStandards,
      schoolName: data.brand_standards?.school_name || "",
      createdAt: new Date(data.brand_standards?.created_at),
      modifiedAt: new Date(data.brand_standards?.modified_at),
    },
  };

  if (data?.brand_colors && data?.brand_colors?.length > 0) {
    newBrandStandards.BrandColors = data?.brand_colors.map((color) => ({
      brandStandardsId: color?.brand_standards_id,
      createdAt: new Date(color.created_at),
      hexColorValue: color.hex_color_value,
      pmsColorValue: color.pms_color_value,
      idBrandColors: color.idBrandColors,
      modifiedAt: new Date(color.modified_at),
    }));
  }

  if (data?.brand_expressions && data?.brand_expressions?.length > 0) {
    newBrandStandards.BrandExpressions = data?.brand_expressions.map((exp) => ({
      brandStandardsId: exp.brand_standards_id,
      createdAt: new Date(exp.created_at),
      expression: exp.expression,
      idBrandExpression: exp.idBrandExpression,
      modifiedAt: new Date(exp.modified_at),
    }));
  }

  if (data?.brand_fonts && data?.brand_fonts?.length > 0) {
    newBrandStandards.BrandFonts = data?.brand_fonts.map((font) => ({
      brandStandardsId: font.brand_standards_id,
      createdAt: new Date(font.created_at),
      fontFileUrl: font.font_file_url,
      fontName: font.font_name,
      idBrandFonts: font.idBrandFonts,
      modifiedAt: new Date(font.modified_at),
    }));
  }

  if (data?.brand_logos && data?.brand_logos?.length > 0) {
    newBrandStandards.BrandLogos = data?.brand_logos.map((logo) => ({
      brandStandardsId: logo.brand_standards_id,
      createdAt: new Date(logo.created_at),
      idBrandLogos: logo.idBrandLogos,
      logoUrl: logo.logo_url,
      modifiedAt: new Date(logo.modified_at),
    }));
  }

  return newBrandStandards;
}

export function calculateBrandStandardsProgress(
  brandStandards: BrandStandards
): number {
  let completionPercentage = 0;
  if (!brandStandards) return 0;
  if (
    brandStandards.BrandColors &&
    brandStandards.BrandColors.length > 0 &&
    brandStandards.BrandColors.some((item) => item.hexColorValue !== "")
  ) {
    completionPercentage += 50;
  }

  if (brandStandards.BrandLogos && brandStandards.BrandLogos.length > 0) {
    completionPercentage += 50;
  }

  return completionPercentage;
}

export function isBrandStandardsComplete(
  brandStandards: BrandStandards
): boolean {
  if (!brandStandards) return false;

  const hasColors = !!(
    brandStandards.BrandColors &&
    brandStandards.BrandColors.length > 0 &&
    brandStandards.BrandColors.some((item) => item.hexColorValue !== "")
  );
  const hasLogos = !!(
    brandStandards.BrandLogos && brandStandards.BrandLogos.length > 0
  );
  const hasFile = !!brandStandards.BrandStandards.brandStandardsFileUrl;

  return hasFile || (hasColors && hasLogos);
}

export function hasBrandStandardsFile(brandStandards: BrandStandards): boolean {
  if (!brandStandards) return false;

  return !!brandStandards.BrandStandards.brandStandardsFileUrl;
}

export function hasBrandStandardsColorAndLogo(
  brandStandards: BrandStandards
): boolean {
  if (!brandStandards) return false;

  const hasColors = !!(
    brandStandards.BrandColors &&
    brandStandards.BrandColors.length > 0 &&
    brandStandards.BrandColors.some((item) => item.hexColorValue !== "")
  );

  const hasLogos = !!(
    brandStandards.BrandLogos && brandStandards.BrandLogos.length > 0
  );

  return hasColors && hasLogos;
}

export function extractBrandStandardsFormData(formData) {
  console.log(`formData`, formData);
  const {
    colorCodes,
    logoFiles,
    fontFiles,
    tones,
    usage,
    selectedOption,
    schoolName,
    idBrandStandards,
  } = formData;

  const formDataToSubmit = new FormData();

  if (idBrandStandards) {
    formDataToSubmit.append("idBrandStandards", idBrandStandards.toString());
  }

  formDataToSubmit.append("school_name", schoolName);

  if (!selectedOption || selectedOption === "Manual") {
    const pms_color_values = colorCodes.map((color) => color.pms);
    const hex_color_values = colorCodes.map((color) => color.hex);

    const newLogoFiles = logoFiles.filter((item) => !item.isExisting);
    const logos = newLogoFiles.map((item) => item.file);

    const newFontFiles = fontFiles.filter((item) => !item.isExisting);
    const fonts = newFontFiles.map((item) => item.file);
    const font_names = newFontFiles.map((item) => item.fontName);

    const brand_expressions = tones.filter((tone) => tone.trim() !== "");

    formDataToSubmit.append("brand_usage", usage);

    if (font_names.length > 0) {
      font_names.forEach((name) => {
        formDataToSubmit.append("font_names[]", name);
      });
    }

    if (hex_color_values.length > 0) {
      hex_color_values.forEach((color) => {
        formDataToSubmit.append("hex_color_values[]", color);
      });
    }

    if (pms_color_values.length > 0) {
      pms_color_values.forEach((color) => {
        formDataToSubmit.append("pms_color_values[]", color);
      });
    }

    if (brand_expressions.length > 0) {
      brand_expressions.forEach((expression) => {
        formDataToSubmit.append("brand_expressions[]", expression);
      });
    }

    if (fonts.length > 0) {
      fonts.forEach((font) => {
        formDataToSubmit.append("fonts", font);
      });
    }

    if (logos.length > 0) {
      logos.forEach((logo) => {
        formDataToSubmit.append("logos", logo);
      });
    }
  } else if (selectedOption === "Upload" && formData.pdfFile) {
    formDataToSubmit.append("pdf_file", formData.pdfFile);
  }

  const formDataObj = {};
  formDataToSubmit.forEach((value, key) => {
    formDataObj[key] = value;
  });

  console.log(`formDataObj`, formDataObj);

  return formDataToSubmit;
}

export function extractLicenseMockupFormData(formData) {
  console.log(`formData`, formData);

  const {
    skuName,
    schoolName,
    color,
    productType,
    state,
    logoApplication,
    media,
    comments,
  } = formData;

  console.log(`comments`, comments);

  const formDataToSubmit = new FormData();

  formDataToSubmit.append("school_name", schoolName);
  formDataToSubmit.append("sku_name", skuName);
  formDataToSubmit.append("color", color);
  formDataToSubmit.append("product_type", productType);
  formDataToSubmit.append("state", state);
  formDataToSubmit.append("logo_application", logoApplication);

  if (comments && comments.length > 0) {
    comments.reverse().forEach((comment, index) => {
      console.log(`Comment index ${index}`, comment);
      formDataToSubmit.append("commenter_name[]", comment.user);
      formDataToSubmit.append("comment_text[]", comment.comment);
    });
  }

  if (media.length > 0) {
    media.forEach((m, index) => {
      formDataToSubmit.append("media", m);
    });
  }

  const formDataObj = {};
  let i = 0;
  formDataToSubmit.forEach((value, key) => {
    if (formDataObj[key]) {
      formDataObj[`${key}-${i}`] = value;
    } else {
      formDataObj[key] = value;
    }

    i++;
  });

  console.log(`formDataObj`, formDataObj);

  console.log(`formDataToSubmit`, formDataToSubmit);

  return formDataToSubmit;
}

export const downloadFileFromUrl = async (url: string, fileName: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error(`Error download file:`, error);
  }
};

// Cloudflare Images on this account only serves NAMED variants ("original",
// "thumbnail" = 128x128, "shopify" = 1080x1080); flexible `w=...` URLs return
// 403 and `WxH/filters:...` returns 400. The variant is always the last path
// segment, so swap it rather than matching a specific variant name.
export const getThumbnailUrl = (url: string): string => {
  if (!url || !url.includes("imagedelivery.net")) return url;

  const [path, query] = url.split("?");
  const swapped = path.replace(/\/[^/]+$/, "/thumbnail");
  return query ? `${swapped}?${query}` : swapped;
};

export function getLastUpdatedTimestamp(item: ComplianceMerchItem): Date {
  let mostRecentTimestamp: Date | null = null;

  if (item.versions && item.versions.length > 0) {
    item.versions.forEach((version) => {
      // Check version's own timestamps
      if (
        version.modifiedAt &&
        (!mostRecentTimestamp || version.modifiedAt > mostRecentTimestamp)
      ) {
        mostRecentTimestamp = version.modifiedAt;
      }

      if (
        version.createdAt &&
        (!mostRecentTimestamp || version.createdAt > mostRecentTimestamp)
      ) {
        mostRecentTimestamp = version.createdAt;
      }

      // Check all comments in this version
      version.comments.forEach((comment) => {
        if (
          comment.timestamp &&
          (!mostRecentTimestamp || comment.timestamp > mostRecentTimestamp)
        ) {
          mostRecentTimestamp = comment.timestamp;
        }
      });
    });
  }

  if (!mostRecentTimestamp) {
    mostRecentTimestamp = new Date();
  }

  return mostRecentTimestamp;
}

export const isLegacyLicense = (collegeData: College) => {
  if (!collegeData?.licenseeInfo) {
    return false;
  }

  return collegeData.licenseeInfo.signerName === "Legacy";
};

/** Color Helper Functions */

/**
 * Ensures a hex color starts with '#' prefix
 * @param color - The hex color string (with or without #)
 * @returns The hex color with '#' prefix
 */
export const ensureHexPrefix = (color: string): string => {
  if (!color || typeof color !== "string") return color;
  const trimmedColor = color.trim();
  if (trimmedColor && !trimmedColor.startsWith("#")) {
    return `#${trimmedColor}`;
  }
  return trimmedColor;
};

/**
 * Normalizes school colors by ensuring all colors have '#' prefix
 * @param college - The college object to normalize colors for
 * @returns The college object with normalized school colors
 */
export const normalizeCollegeColors = (college: College): College => {
  if (!college?.schoolColors) return college;

  try {
    const colors = college.schoolColors.split(",");
    const normalizedColors = colors.map((color) => ensureHexPrefix(color));

    return {
      ...college,
      schoolColors: normalizedColors.join(","),
    };
  } catch (error) {
    console.error("Error normalizing college colors:", error);
    return college;
  }
};

/**
 * Normalizes an array of colleges' school colors
 * @param colleges - Array of college objects
 * @returns Array of college objects with normalized school colors
 */
export const normalizeCollegesColors = (colleges: College[]): College[] => {
  if (!colleges || !Array.isArray(colleges)) return colleges;

  return colleges.map((college) => normalizeCollegeColors(college));
};

function calculateContrastRatio(hexColor: string, textColor: string) {
  if (!hexColor) return 4.5;

  const getLuminance = (hex: string) => {
    if (!hex) return 4.5;

    const rgb = hex
      .replace("#", "")
      .match(/.{2}/g)
      ?.map((x) => parseInt(x, 16) / 255);

    if (!rgb) return 4.5;

    const [r, g, b] = rgb?.map((v) =>
      v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
    );

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const backgroundLuminance = getLuminance(hexColor);
  const textLuminance = getLuminance(
    textColor === "black" ? "#000000" : "#ffffff"
  );

  const contrastRatio =
    (Math.max(backgroundLuminance, textLuminance) + 0.05) /
    (Math.min(backgroundLuminance, textLuminance) + 0.05);

  return contrastRatio;
}

export default function getAccessibleTextColor(backgroundColor: string) {
  const whiteContrast = calculateContrastRatio(backgroundColor, "white");

  const passedContrast = whiteContrast >= 4.5;

  return {
    textColor: passedContrast ? "#f5f5f5" : "#2d2d2d",
    secondaryTextColor: passedContrast ? "#B5B5B5" : "#333333",
  };
}

export const lightenColor = (color: string, percent: number) => {
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);

  const newR = Math.min(255, Math.round(r + (255 - r) * (percent / 100)));
  const newG = Math.min(255, Math.round(g + (255 - g) * (percent / 100)));
  const newB = Math.min(255, Math.round(b + (255 - b) * (percent / 100)));

  return (
    newR.toString(16).padStart(2, "0") +
    newG.toString(16).padStart(2, "0") +
    newB.toString(16).padStart(2, "0")
  );
};

export const darkenColor = (color: string, percent: number) => {
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);

  const newR = Math.max(0, Math.round(r * (1 - percent / 100)));
  const newG = Math.max(0, Math.round(g * (1 - percent / 100)));
  const newB = Math.max(0, Math.round(b * (1 - percent / 100)));

  return (
    newR.toString(16).padStart(2, "0") +
    newG.toString(16).padStart(2, "0") +
    newB.toString(16).padStart(2, "0")
  );
};

export const hexToRgb = (hex: string): RGBColor | null => {
  hex = hex.replace("#", "");

  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;

  return { r, g, b };
};

export const rgbToHex = (r: number, g: number, b: number) => {
  return (
    "#" +
    ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()
  );
};

export const getContrastColor = (hexColor: string) => {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return "#000000";

  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;

  return brightness < 128 ? "#FFFFFF" : "#000000";
};

/** Date Helper functions */

export const formatRelativeDate = (date: Date): string => {
  const now = new Date();
  const inputDate = new Date(date);

  const diffMs = now.getTime() - inputDate.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) {
    return "Just now";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`;
  }

  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
  }

  if (diffDays < 7) {
    return `${diffDays} ${diffDays == 1 ? "day" : "days"} ago`;
  }

  return inputDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

function convertToLocalTime(centralTimeString) {
  const timeWithoutZ = centralTimeString.replace("Z", "");

  const [year, month, day] = timeWithoutZ.split("T")[0].split("-").map(Number);
  const [hour, minute, second] = timeWithoutZ
    .split("T")[1]
    .split(":")
    .map(Number);

  const testDate = new Date(
    Date.UTC(year, month - 1, day, hour, minute, second)
  );

  const isDST = isDaylightSavingTime(testDate);

  const centralOffset = isDST ? -5 : -6;

  const utcMillis = Date.UTC(
    year,
    month - 1,
    day,
    hour - centralOffset,
    minute,
    second
  );

  return new Date(utcMillis);
}

function isDaylightSavingTime(date) {
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();

  if (month > 3 && month < 11) {
    return true; // April through October
  } else if (month < 3 || month > 11) {
    return false; // January, February, December
  }

  // March: DST starts on the second Sunday
  if (month === 3) {
    const firstDay = new Date(Date.UTC(date.getUTCFullYear(), 2, 1));
    const firstDayOfWeek = firstDay.getUTCDay();
    const secondSunday = firstDayOfWeek === 0 ? 8 : 15 - firstDayOfWeek;

    return day >= secondSunday;
  }

  // November: DST ends on the first Sunday
  if (month === 11) {
    const firstDay = new Date(Date.UTC(date.getUTCFullYear(), 10, 1));
    const firstDayOfWeek = firstDay.getUTCDay();
    const firstSunday = firstDayOfWeek === 0 ? 1 : 8 - firstDayOfWeek;

    return day < firstSunday;
  }

  // Fallback (shouldn't reach here)
  return false;
}

export const isDateInRange = (
  value: Date | string | number | null | undefined,
  filter: { startDate: Date | null; endDate: Date | null }
): boolean => {
  if (!value) return false;
  if (!filter.startDate && !filter.endDate) return true;

  let dateValue: Date;

  if (value instanceof Date) {
    dateValue = value;
  } else if (typeof value === "string") {
    dateValue = new Date(value);
  } else if (typeof value === "number") {
    dateValue = new Date(value);
  } else {
    return false;
  }

  if (isNaN(dateValue.getTime())) return false;

  dateValue.setHours(0, 0, 0, 0);

  if (filter.startDate) {
    const startDate = new Date(filter.startDate);
    startDate.setHours(0, 0, 0, 0);
    if (dateValue < startDate) return false;
  }

  if (filter.endDate) {
    const endDate = new Date(filter.endDate);
    endDate.setHours(23, 59, 59, 999);
    if (dateValue > endDate) return false;
  }

  return true;
};

/** Formatting Helper functions */

export const formatFileSize = (bytes: number): string => {
  const megabytes = bytes / (1024 * 1024);

  if (megabytes < 0.1) {
    const kilobytes = bytes / 1024;
    return `${Math.round(kilobytes)} KB`;
  }

  return `${megabytes.toFixed(1)} MB`;
};

export const serializeFilters = (filters: FilterRecord): string => {
  const serialized = Object.entries(filters)
    .map(([key, value]) => {
      if (typeof value === "string") {
        return `${encodeURIComponent(key)}:s:${encodeURIComponent(value)}`;
      } else if (
        typeof value === "object" &&
        "min" in value &&
        "max" in value
      ) {
        return `${encodeURIComponent(key)}:r:${value.min}:${value.max}`;
      } else if (
        typeof value === "object" &&
        "startDate" in value &&
        "endDate" in value
      ) {
        const startDateStr = value.startDate
          ? value.startDate.toISOString()
          : "";
        const endDateStr = value.endDate ? value.endDate.toISOString() : "";
        return `${encodeURIComponent(key)}:d:${encodeURIComponent(
          startDateStr
        )}:${encodeURIComponent(endDateStr)}`;
      }
      return "";
    })
    .filter(Boolean)
    .join(",");

  return serialized;
};

export const deserialzeFilters = (filterStr: string): FilterRecord => {
  const filters: FilterRecord = {};

  if (!filterStr) return filters;

  const filterParts = filterStr.split(",");

  filterParts.forEach((part) => {
    const segments = part.split(":");

    if (segments.length >= 3) {
      const key = decodeURIComponent(segments[0]);
      const type = segments[1];

      if (type === "s") {
        // String filter
        filters[key] = decodeURIComponent(segments[2]);
      } else if (type === "r" && segments.length >= 4) {
        // Range filter
        filters[key] = {
          min: parseFloat(segments[2]),
          max: parseFloat(segments[3]),
        };
      } else if (type === "d" && segments.length >= 4) {
        // Date range filter
        const startDateStr = decodeURIComponent(segments[2]);
        const endDateStr = decodeURIComponent(segments[3]);

        filters[key] = {
          startDate: startDateStr ? new Date(startDateStr) : null,
          endDate: endDateStr ? new Date(endDateStr) : null,
        };
      }
    }
  });

  return filters;
};

export const formatUSPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return "";

  const digitsOnly = phoneNumber.replace(/\D/g, "");

  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(
      3,
      6
    )}-${digitsOnly.slice(6)}`;
  } else if (digitsOnly.length === 11) {
    const without1 = digitsOnly.slice(1);
    return `(${without1.slice(0, 3)}) ${without1.slice(3, 6)}-${without1.slice(
      6
    )}`;
  } else if (digitsOnly.length === 7) {
    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
  }

  return phoneNumber;
};

export function convertToCSTWithTime(dateString: string) {
  if (!dateString) return "-";

  const cleanDate = dateString.replace("Z", "");
  const [datePart, timePart] = cleanDate.split("T");
  const [year, month, day] = datePart.split("-");
  const [hour, minute, second] = timePart.split(":");

  const cstDate = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hour),
    parseInt(minute),
    parseInt(second.split(".")[0])
  );

  const actualUTCTime = cstDate.getTime() + 6 * 60 * 60 * 1000;
  const correctedDate = new Date(actualUTCTime);

  const timeZoneName =
    Intl.DateTimeFormat("en", {
      timeZoneName: "short",
    })
      .formatToParts(correctedDate)
      .find((part) => part.type === "timeZoneName")?.value || "";

  const formatted = correctedDate.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${formatted} ${timeZoneName}`;
}

export function convertToCST(dateString: string) {
  if (!dateString) return "-";

  const cleanDate = dateString.replace("Z", "");
  const [datePart, timePart] = cleanDate.split("T");
  const [year, month, day] = datePart.split("-");
  const [hour, minute, second] = timePart.split(":");

  const cstDate = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hour),
    parseInt(minute),
    parseInt(second.split(".")[0])
  );

  const actualUTCTime = cstDate.getTime() + 6 * 60 * 60 * 1000;
  const correctedDate = new Date(actualUTCTime);

  const formatted = correctedDate.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return `${formatted}`;
}
